import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import * as Router from "react-router-dom";
import moment, { Moment } from "moment";
import { AppDatePicker } from "component/shared/date-picker";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { Table, Column, Sort } from "component/shared/table";
import { parseDateParam } from "component/shared/report-filter-params";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import {
	getSavedListingsReportRows,
	getSavedListingsReportLoading,
	getSavedListingsReportLoaded,
} from "redux/selector";
import { fetchSavedListingsReport } from "redux/slice/saved-listings-report";
import { SavedListingReportResource } from "api/resource/saved-listing-report";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Saved Properties Activity";

const formatShortDate = (iso: string): string => (iso ? moment(iso).format("M/D/YY") : "");

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.reporting}
		title={TITLE}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.BarChart2 />
				</FeatherIcon>
				{TITLE}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/statsSavedProperties.cfm" />}
	/>
);

const ReportsSavedListingsBase: React.FC<Props> = ({ user, classes = {}, location, history }) => {
	const dispatch = useDispatch();
	const rows = useSelector((state: RootState) => getSavedListingsReportRows(state));
	const loading = useSelector((state: RootState) => getSavedListingsReportLoading(state));
	const loaded = useSelector((state: RootState) => getSavedListingsReportLoaded(state));

	const [startDate, setStartDate] = useState<Moment>(() => parseDateParam(location.search, "startDate") ?? moment().subtract(3, "days"));
	const [endDate, setEndDate] = useState<Moment>(() => parseDateParam(location.search, "endDate") ?? moment());
	// Default sort matches CF page default (dateStamp DESC). Client-side sort/paging via the shared Table.
	const [sort, setSort] = useState<Sort>({ id: "dateSaved", desc: true });
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: "" });

	const loadReport = React.useCallback(async () => {
		try {
			// F4: .unwrap() so an API failure rejects here and surfaces an error snackbar.
			await dispatch(
				fetchSavedListingsReport({
					startDate: startDate.format("YYYY-MM-DD"),
					endDate: endDate.format("YYYY-MM-DD"),
				}) as any
			).unwrap();
		} catch{
			setSnackbar({ open: true, message: "Failed to load report" });
		}
	}, [dispatch, startDate, endDate]);

	useEffect(() => {
		loadReport();
	}, [dispatch]); // eslint-disable-line

	// Reflect the applied date range in the URL so it persists across back-nav / refresh / share
	// (spec-directed URL query params in place of CF's per-client session vars).
	const handleSubmit = () => {
		history.replace({
			search: new URLSearchParams({
				startDate: startDate.format("YYYY-MM-DD"),
				endDate: endDate.format("YYYY-MM-DD"),
			}).toString(),
		});
		loadReport();
	};

	const columns: Column<SavedListingReportResource>[] = [
		{
			id: "name",
			title: "Name",
			value: (row) => (
				<>
					<Mui.Typography variant="body2" className={classes.nameLabel}>
						{row.subscriberName}
					</Mui.Typography>
					<Mui.Typography variant="body2" color="textSecondary">
						{row.subscriberEmail}
					</Mui.Typography>
				</>
			),
			sort: (a, b) => (a.subscriberName || "").localeCompare(b.subscriberName || ""),
		},
		{
			id: "dateSaved",
			title: "Date Saved",
			value: (row) => formatShortDate(row.dateSaved),
			sort: (a, b) => moment(a.dateSaved).valueOf() - moment(b.dateSaved).valueOf(),
		},
		{
			id: "mlsNumber",
			title: "MLS #",
			value: (row) => row.mlsNumber,
			sort: (a, b) => (a.mlsNumber || "").localeCompare(b.mlsNumber || ""),
		},
		{
			id: "address",
			title: "Address",
			value: (row) => (
				<Mui.Link
					href={row.listingDetailUrl}
					target="_blank"
					rel="noopener noreferrer"
					data-testid={`saved-listing-address-${row.leadId}`}
				>
					{row.address}
				</Mui.Link>
			),
			sort: (a, b) => (a.address || "").localeCompare(b.address || ""),
		},
	];

	const renderResults = () => {
		if (loaded && rows.length === 0) {
			return (
				<MuiAlert severity="info">
					{/* CF format is mm/dd/yyyy (StatsSavedProperties1.cfm:41,180-184) — match it and the
						page's own date pickers (format="MM/dd/yyyy") rather than the terse M/D/YY. */}
					No Matches for Date Range. {startDate.format("MM/DD/YYYY")} - {endDate.format("MM/DD/YYYY")}
				</MuiAlert>
			);
		}
		return (
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid item>
					<Mui.Typography variant="h6">Saved Properties Results ({rows.length})</Mui.Typography>
				</Mui.Grid>
				<Mui.Grid item>
					{/* Full list from the endpoint; the shared Table pages + sorts client-side (default
						10/page — matches CF DataTables). */}
					<Table items={rows} columns={columns} sort={sort} onSortChange={setSort} />
				</Mui.Grid>
			</Mui.Grid>
		);
	};

	return (
		<DashboardLayout
			permitted={user && user.permissions.reporting}
			title={TITLE}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.BarChart2 />
					</FeatherIcon>
					{TITLE}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				<Mui.Grid container spacing={2} alignItems="flex-end" className={classes.filterBar}>
					<Mui.Grid item xs={6} sm={3} data-testid="saved-listing-filter-start">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="Start Date"
								placeholder="mm/dd/yyyy"
								value={startDate.toDate()}
								onChange={(date) => setStartDate(date ? moment(date) : moment())}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item xs={6} sm={3} data-testid="saved-listing-filter-end">
						<AppDatePicker
								format="MM/dd/yyyy"
								fullWidth
								label="End Date"
								placeholder="mm/dd/yyyy"
								value={endDate.toDate()}
								onChange={(date) => setEndDate(date ? moment(date) : moment())}
								openPickerIcon={<Icons.Calendar size={20} />}
								views={["day"]}
							/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={handleSubmit}
							data-testid="saved-listing-filter-submit"
						>
							Submit
						</Mui.Button>
					</Mui.Grid>
				</Mui.Grid>

				{loading && !loaded ? <Mui.CircularProgress /> : renderResults()}
			</FullPageCard>

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity="error">
					<Mui.Typography>{snackbar.message}</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const ReportsSavedListings = withStyles(ReportsSavedListingsBase, styles);

const ReportsSavedListingsPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <ReportsSavedListings {...props} />;
};

export const ReportsSavedListingsPage = withStyles(ReportsSavedListingsPageBase, styles);
